// Importing necessary components from Material-UI and React
import {
  Dialog,
  useTheme,
  Box,
  Typography,
  Stack,
  DialogActions,
  IconButton,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";

// Importing CloseIcon from Material-UI Icons library
import CloseIcon from "@mui/icons-material/Close";

// Default colors for the modal background
const defaultColors = ["#FEF3F2", "#FEE4E2"];

// Functional component for a confirmation modal
const ConfirmModalTemplate = ({
  actions, // Action buttons for the modal
  mainText = "", // Main text content of the modal
  secondaryText = "", // Secondary text content of the modal
  open, // Boolean to control modal visibility
  onClose, // Function to close the modal
  maxWidth = "sm", // Maximum width of the modal
  fullWidth = true, // Whether the modal should take full width
  iconType = "delete", // Type of icon to be displayed in the modal
  colors = defaultColors, // Background colors for the modal
}) => {
  // Accessing the theme from Material-UI
  const theme = useTheme();

  // Generating the appropriate icon based on the specified iconType
  const Icons = useMemo(
    () =>
      iconType === "delete" ? (
        // Trash icon with red color
        <i
          class="fi fi-rs-trash"
          style={{ color: "#d13c31", fontSize: 24 }}
        ></i>
      ) : iconType === "save" ? (
        // Checkmark icon with the primary theme color
        <i
          class="fi fi-sr-check"
          style={{ color: theme.palette.primary.main, fontSize: 24 }}
        ></i>
      ) : null,
    [iconType]
  );

  // useEffect to check for required props
  useEffect(() => {
    if (!onClose) {
      throw new Error(
        "Error: Missing 'onClose' function for delete dialog. Please import it."
      );
    }
    if (!actions) {
      throw new Error(
        'Error: Actions buttons not found. Please import "actions"'
      );
    }
  }, [onClose, actions]);

  // Rendering the confirmation modal
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      {/* Modal content */}
      <Box sx={{ position: "relative", padding: "24px" }}>
        {/* Header section with icon and close button */}
        <Stack direction={"row"} justifyContent={"space-between"}>
          <div
            className="delete-modal-icon"
            style={{ backgroundColor: colors[0] }}
          >
            <div
              style={{
                backgroundColor: colors[1],
              }}
            >
              {Icons}
            </div>
          </div>
          <IconButton onClick={onClose} size="large" sx={{ mt: -2, mr: -2 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        {/* Main and secondary text content */}
        <Stack sx={{ mt: 1.3 }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: "18px", lineHeight: "28px" }}
          >
            {mainText}
          </Typography>
          <Typography
            sx={{ fontSize: "14px", lineHeight: "20px", fontWeight: 400 }}
          >
            {secondaryText}
          </Typography>
        </Stack>
      </Box>
      {/* Action buttons section */}
      <DialogActions sx={{ justifyContent: "flex-start", px: 3, pb: 3 }}>
        {actions}
      </DialogActions>
    </Dialog>
  );
};

// Exporting the ConfirmModalTemplate component
export default ConfirmModalTemplate;
